import React from 'react';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { ModalBody } from '../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Separator } from '../../../../CommonComponents/OPL/Separator/Separator';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency, formatPercentage } from '../../../../CommonUtils/formatNumber';
import { TaxModeDTO, useWithdrawMutation, WithdrawParams } from '../../api/useWithdrawMutation';
import { WithdrawMetadata } from './components/WithdrawForm/useWithdrawForm';
import { DISTRIBUTION_REASONS_TITLES } from './components/WithdrawSelection/WithdrawSelection.meta';

interface Props {
  onBack(): void;
  onClose(): void;
  onSuccess(): void;
  withdrawDetails: { params: WithdrawParams; metadata: WithdrawMetadata };
}

export const WithdrawPreview: React.FC<Props> = ({
  onClose,
  onBack,
  onSuccess,
  withdrawDetails,
}) => {
  const { mutateAsync, isLoading, error } = useWithdrawMutation();

  const onSubmit = async () => {
    try {
      await mutateAsync(withdrawDetails.params);

      onSuccess();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box>
      <ModalBody>
        {error && <Alert variant="error" message={error.message} mb="16" />}

        <Text color="neutral.500" my="16">
          Please review your transfer details
        </Text>

        <Separator mx={-24} />

        <Box
          as="dl"
          py="16"
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text as="dt" color="neutral.500" fontWeight={500}>
            Transfer from
          </Text>
          <Text as="dd" fontWeight={500}>
            {withdrawDetails.metadata.sourceAccount}
          </Text>
        </Box>

        <Separator mx={-24} />

        <Box
          as="dl"
          py="16"
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text as="dt" color="neutral.500" fontWeight={500}>
            Transfer to
          </Text>
          <Text as="dd" fontWeight={500}>
            {withdrawDetails.metadata.destinationAccount}
          </Text>
        </Box>

        <Separator mx={-24} />

        <Box
          as="dl"
          py="16"
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text as="dt" color="neutral.500" fontWeight={500}>
            Amount
          </Text>
          <Text as="dd" fontWeight={500}>
            {formatCurrency(withdrawDetails.params.amount)}
          </Text>
        </Box>

        <Separator mx={-24} />

        {withdrawDetails.params.iraDistribution?.distributionReason && (
          <>
            <Box
              as="dl"
              py="16"
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Text as="dt" color="neutral.500" fontWeight={500}>
                Distribution Reason
              </Text>
              <Text as="dd" fontWeight={500}>
                {
                  DISTRIBUTION_REASONS_TITLES[
                    withdrawDetails.params.iraDistribution.distributionReason
                  ]
                }
              </Text>
            </Box>

            <Separator mx={-24} />
          </>
        )}

        {withdrawDetails.params.iraDistribution?.federalTaxWithholding?.amountToWithhold && (
          <>
            <Box
              as="dl"
              py="16"
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Text as="dt" color="neutral.500" fontWeight={500}>
                Federal Tax Withholding
              </Text>
              <Text as="dd" fontWeight={500}>
                {withdrawDetails.params.iraDistribution.federalTaxWithholding.valueType ===
                TaxModeDTO.Amount
                  ? formatCurrency(
                      withdrawDetails.params.iraDistribution.federalTaxWithholding.amountToWithhold,
                      { decimalPlaces: 2 },
                    )
                  : formatPercentage(
                      withdrawDetails.params.iraDistribution.federalTaxWithholding
                        .amountToWithhold / 100,
                      { decimalPlaces: 2 },
                    )}
              </Text>
            </Box>

            <Separator mx={-24} />
          </>
        )}

        {withdrawDetails.params.iraDistribution?.stateTaxWithholding?.amountToWithhold && (
          <>
            <Box
              as="dl"
              py="16"
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Text as="dt" color="neutral.500" fontWeight={500}>
                State Tax Withholding
              </Text>
              <Text as="dd" fontWeight={500}>
                {withdrawDetails.params.iraDistribution.stateTaxWithholding.valueType ===
                TaxModeDTO.Amount
                  ? formatCurrency(
                      withdrawDetails.params.iraDistribution.stateTaxWithholding.amountToWithhold,
                      { decimalPlaces: 2 },
                    )
                  : formatPercentage(
                      withdrawDetails.params.iraDistribution.stateTaxWithholding.amountToWithhold /
                        100,
                      { decimalPlaces: 2 },
                    )}
              </Text>
            </Box>

            <Separator mx={-24} />
          </>
        )}

        <Text fontWeight={600} mt="16">
          Note:
        </Text>
        <Text as="dt" color="neutral.500" fontWeight={500}>
          {withdrawDetails.metadata.note}
        </Text>
      </ModalBody>
      <ModalFooter>
        <Box flex={1}>
          <Button leftIcon="fas fa-chevron-left" variant="secondary" onClick={onBack}>
            Back
          </Button>
        </Box>

        <Button variant="subtleInactive" onClick={onClose} mr="8">
          Cancel
        </Button>

        <Button onClick={onSubmit} isLoading={isLoading}>
          Transfer
        </Button>
      </ModalFooter>
    </Box>
  );
};
